import FileSaver from 'file-saver';
import moment from 'moment';
import {enqueueReportJob} from '../services/reportService';
import {formatDuration} from './duration';

const EXPORT_MIME = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8',
  pdf: 'application/pdf',
};

/**
 * `job.reportType` (fila de exportação) -> chave de i18n do nome curto do tipo. Usado tanto na
 * coluna "Relatório" de /reportjobs quanto no card de notificação de relatório pronto.
 */
export const REPORT_TYPE_LABEL = {
  route: 'reportRoute',
  trips: 'reportTrips',
  stops: 'reportStops',
  journey: 'reportJourney',
  events: 'reportEvents',
  summary: 'reportSummary',
};

/**
 * The e-mail attachment is always the spreadsheet; only an explicit PDF request changes the format.
 */
export const reportExportFormat = (outer) => (outer === 'pdf' ? 'pdf' : 'xlsx');

/**
 * Saves a report response under the given name, with the extension of the format it came in.
 */
export const saveReportFile = (response, format, baseName) => {
  // A resposta já chega como Blob, porque o serviço pede `responseType: 'blob'`. Embrulhar de novo
  // em `new Blob([response])` copiaria o arquivo inteiro na memória: com relatório grande são dezenas
  // de megabytes duplicados, com a aba já carregada.
  const blob = response instanceof Blob ?
    response :
    new Blob([response], {type: EXPORT_MIME[format]});

  FileSaver.saveAs(blob, `${baseName}.${format}`);
};

const DAY_IN_MILLIS = 86400000;

// Cores dos dispositivos no relatório. São as mesmas amostras que o seletor de cor da configuração
// da central oferece (as predefinidas do SketchPicker), para o sistema não ter duas paletas.
//
// Cinco das quinze ficam de fora porque o mapa não as comporta: branco, amarelo e verde pálido
// somem sobre o mapa claro; o cinza claro se confunde com via; e o vermelho puro é a cor do ponto
// selecionado, então usar no traço confundiria seleção com veículo. A ordem começa pelo azul, que é
// o mais próximo do que o relatório já desenhava.
export const REPORT_DEVICE_COLORS = [
  '#4A90E2',
  '#F5A623',
  '#417505',
  '#9013FE',
  '#50E3C2',
  '#8B572A',
  '#BD10E0',
  '#7ED321',
  '#4A4A4A',
];

// Cor do dispositivo pela posição dele no relatório. Determinística: o mesmo dispositivo mantém a
// cor entre um desenho e outro, e o traço combina com os marcadores dele e com o nome na lista.
// Acima do tamanho da paleta ela repete -- inventar tom novo ali sairia perto demais de outro.
export const reportDeviceColor = (index) =>
  REPORT_DEVICE_COLORS[Math.abs(index) % REPORT_DEVICE_COLORS.length];

// Mesma cor, mais clara. Usada no marcador não selecionado do relatório: o ponto escolhido fica com
// a cor cheia e salta no meio dos outros, sem trocar de cor e sem perder de qual veículo ele é.
export const fadeColor = (color, alpha = 0.45) => {
  const hex = String(color).replace('#', '');
  if (hex.length !== 6) return color;

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Teto de marcadores desenhados no mapa por relatório. Medido no ambiente de testes: cerca de 5,4 mil
// marcadores de um único dispositivo já deixam a navegação lenta na máquina do servidor, e 12 mil
// travam até em máquina forte. O traço da rota continua inteiro -- o que o teto corta é o desenho de
// um marcador por posição, que cria uma feição e um estilo para cada uma delas.
export const REPORT_MARKER_LIMIT = 4000;

// Passo mínimo quando o relatório passa do teto. Um a cada vinte mantém a rota marcada de ponta a
// ponta com espaçamento que não se nota no zoom de um trajeto de dias, e no uso real -- quatro
// veículos na mesma rota, sete dias, 24.836 posições -- dá cerca de mil e duzentos marcadores.
export const REPORT_MARKER_STEP = 20;

/**
 * De quantas em quantas posições desenhar um marcador. Um significa desenhar todas.
 *
 * O formulário não tem como decidir isto antes de gerar, porque quantas posições um período produz
 * depende do quanto cada veículo reportou -- a medição variou entre 766 e 914 por dispositivo por
 * dia. Aqui o resultado já chegou e o número é exato.
 *
 * Relatório que cabe no teto desenha tudo. Acima do teto entra o passo
 * mínimo, e se nem ele couber (acima de oitenta mil posições) o passo cresce o quanto for preciso,
 * para o custo do desenho ficar limitado por mais que cresça o relatório.
 *
 * @param {number} positionCount total de posições do resultado
 * @return {number} passo entre marcadores, `1` quando todos são desenhados
 */
export const reportMarkerStep = (positionCount) => {
  if (positionCount <= REPORT_MARKER_LIMIT) return 1;

  return Math.max(REPORT_MARKER_STEP, Math.ceil(positionCount / REPORT_MARKER_LIMIT));
};

/**
 * Calendar days in the period, rounded up. Same formula as ReportFileName on the server, so both
 * ends of the export agree: today so far is 1, a week is 7, a month is 28 to 31.
 */
export const reportDayCount = (from, to) => {
  if (!from || !to) return 1;
  return Math.max(1, Math.ceil((new Date(to) - new Date(from)) / DAY_IN_MILLIS));
};

/**
 * Longest period a single report may cover. Matches the server-side report.periodLimit, and is 31
 * rather than 30 so the 'last month' and 'current month' presets still fit in months of 31 days.
 */
export const REPORT_MAX_DAYS = 31;

export const exceedsReportPeriodLimit = (from, to) =>
  Boolean(from) && Boolean(to) && reportDayCount(from, to) > REPORT_MAX_DAYS;

/**
 * Name of the exported file: report title, then IMEI or 'many devices', then date or day count.
 *
 * A single day shows the date; more than one shows the number of days instead. One device is
 * named by its IMEI, and any other selection falls back to the many-devices label. A report
 * with no period
 * (the fines snapshot) is dated today.
 */
export const reportFileBaseName = ({intl, titleId, devices = [], deviceIds = undefined, deviceCount, from, to}) => {
  const report = intl.formatMessage({id: titleId});
  const title = intl.formatMessage({id: 'reportFileNamePattern'}, {report});

  let who;
  if (deviceCount !== undefined) {
    // Relatórios Solicitados só recebe a contagem resolvida, não a identidade dos
    // dispositivos -- por isso um número aqui, nunca o IMEI, mesmo quando a contagem é 1.
    who = deviceCount === 1 ?
      intl.formatMessage({id: 'reportFileNameOneDevice'}) :
      intl.formatMessage({id: 'reportFileNameDeviceCount'}, {count: deviceCount});
  } else {
    const onlyDevice = deviceIds && deviceIds.length === 1;
    const selected = onlyDevice &&
      devices.find((device) => String(device.id) === String(deviceIds[0]));
    who = (selected && selected.uniqueId) ||
      intl.formatMessage({id: 'reportFileNameManyDevices'});
  }

  const days = reportDayCount(from, to);
  const when = days > 1 ?
    intl.formatMessage({id: 'reportFileNameDays'}, {count: days}) :
    moment(from || undefined).format('DDMMYYYY');

  return `${title} - ${who} - ${when}`;
};

const REPORT_JOB_ERROR_KEYS = {
  reportNotReady: 'reportJobErrorNotReady',
  reportAlreadyFinished: 'reportJobErrorAlreadyFinished',
  reportTypeUnsupported: 'reportJobErrorUnsupportedType',
  reportFormatUnsupported: 'reportJobErrorUnsupportedFormat',
  reportPeriodInvalid: 'reportJobErrorInvalidPeriod',
  reportNoDevices: 'reportJobErrorNoDevices',
  reportStorageNotConfigured: 'reportJobErrorStorageNotConfigured',
  mailNotConfigured: 'reportEmailNotConfigured',
};

/**
 * Quanto tempo, em milissegundos, o aviso de "pedido aceito" fica na tela. Longo de propósito: a pessoa pode
 * estar olhando outra coisa. O aviso some sozinho, para vários pedidos seguidos não empilharem avisos na tela.
 */
export const REPORT_ENQUEUED_TOAST_MS = 60000;

/**
 * Quanto tempo, em milissegundos, o aviso de "relatório grande demais para o e-mail" fica na tela. Longo de
 * propósito: a pessoa pediu o e-mail e precisa ler que o arquivo está pronto para baixar em outro lugar.
 */
export const REPORT_MAIL_NOTICE_TOAST_MS = 30000;

/**
 * Aviso que o servidor manda junto do pedido (resposta do POST ou evento do socket) quando o e-mail pedido não
 * saiu porque o arquivo passa do tamanho que cabe em anexo. O relatório continua pronto.
 *
 * @param {object} intl instância do react-intl
 * @param {object=} job pedido recebido do servidor
 * @return {string|null} texto traduzido, ou `null` quando não há aviso
 */
export const reportJobMailNotice = (intl, job) =>
  job?.mailNotice === 'mailTooLarge' ? intl.formatMessage({id: 'reportJobMailTooLarge'}) : null;

/**
 * Mostra, no toast padrão do sistema, por `REPORT_MAIL_NOTICE_TOAST_MS`, o aviso de anexo grande de um pedido, se houver.
 *
 * @param {Function} addToast função `addToast` do react-toast-notifications
 * @param {object} intl instância do react-intl
 * @param {object=} job pedido recebido do servidor
 */
// O mesmo aviso chega pela resposta do pedido e pelo evento do socket; cada pedido avisa uma vez só.
const noticedMailJobIds = new Set();

export const notifyReportJobMailNotice = (addToast, intl, job) => {
  const message = reportJobMailNotice(intl, job);
  if (!message) return;
  if (job.id !== undefined) {
    if (noticedMailJobIds.has(job.id)) return;
    noticedMailJobIds.add(job.id);
  }
  addToast(message, {appearance: 'error', autoDismiss: true, autoDismissTimeout: REPORT_MAIL_NOTICE_TOAST_MS});
};

/**
 * Com a cópia no S3 o servidor responde `{url, expiresAt}` em JSON em vez do arquivo. A resposta chega como Blob
 * (o pedido pede `blob`); aqui ela é reconhecida e a URL devolvida. Arquivo de verdade devolve `null`.
 *
 * SECURITY: só aceita URL http ou https; qualquer outro esquema (por exemplo `javascript:`) devolve `null`, porque
 * a URL vira o destino de um link clicado por código.
 *
 * @param {*} response corpo da resposta do download
 * @return {Promise<string|null>} URL assinada, ou `null` quando a resposta é o próprio arquivo
 */
export const reportJobDownloadUrl = async (response) => {
  if (!(response instanceof Blob) || !response.type.startsWith('application/json')) {
    return null;
  }
  try {
    const body = JSON.parse(await readBlobText(response));
    return typeof body?.url === 'string' && /^https?:\/\//i.test(body.url) ? body.url : null;
  } catch {
    return null;
  }
};

/**
 * Abre a URL assinada do S3 para baixar o relatório. Link comum clicado por código: o navegador baixa sem sair da
 * tela, e nenhum cabeçalho de autorização da API vai junto.
 *
 * CONSTRAINT: o atributo `download` é ignorado em endereço de outra origem, como o do S3. Quem faz o navegador
 * baixar, em vez de abrir o arquivo na aba do app, é o servidor, que assina a URL pedindo
 * `Content-Disposition: attachment` (`S3Helper.presignedDownloadUrl`). Se esse cabeçalho deixar de ir na URL, um
 * PDF abriria na própria aba e tiraria a pessoa da tela; a saída seria abrir com `target="_blank"` aqui.
 *
 * @param {string} url URL assinada
 * @param {string} fileName nome sugerido
 */
export const openSignedDownload = (url, fileName) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Texto de erro de uma ação sobre o pedido (pedir, baixar, cancelar). O servidor responde com um código estável em
 * `data.code` (409 ao baixar ou cancelar, 400 e 503 ao pedir); qualquer outro erro, ou código desconhecido, cai na
 * mensagem genérica. O `error` é a própria resposta de erro do servidor: `request()` lança `err.response`.
 *
 * @param {object} intl instância do react-intl
 * @param {*} error resposta de erro do servidor (`status` e `data`)
 * @param {string} fallbackId chave da mensagem genérica quando o erro não traz um código conhecido
 * @return {string} texto traduzido
 */
export const reportJobErrorMessage = (intl, error, fallbackId = 'reportLoadError') =>
  intl.formatMessage({id: REPORT_JOB_ERROR_KEYS[error?.data?.code] || fallbackId});

const readBlobText = (blob) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result));
  reader.onerror = () => resolve('');
  reader.readAsText(blob);
});

/**
 * Igual a `reportJobErrorMessage`, mas lê o corpo quando ele vem como Blob: o download pede
 * `responseType: 'blob'`, e o navegador entrega assim também o JSON de um erro 409.
 *
 * @param {object} intl instância do react-intl
 * @param {*} error resposta de erro do servidor (`status` e `data`)
 * @return {Promise<string>} texto traduzido
 */
export const reportJobDownloadErrorMessage = async (intl, error) => {
  if (!(error?.data instanceof Blob)) {
    return reportJobErrorMessage(intl, error);
  }
  let data;
  try {
    data = JSON.parse(await readBlobText(error.data));
  } catch {
    data = undefined;
  }
  return reportJobErrorMessage(intl, {data});
};

const REPORT_JOB_FAILURE_KEYS = {
  workerDied: 'reportJobFailureWorkerDied',
  generationFailed: 'reportJobFailureGenerationFailed',
};

/**
 * Motivo mostrado num pedido que falhou. O servidor grava um código em `failure`; pedido antigo, com texto
 * livre gravado, também cai na mensagem genérica, nunca mostra o texto cru.
 *
 * @param {object} intl instância do react-intl
 * @param {string=} failure código gravado em `failure`
 * @return {string} texto traduzido
 */
export const reportJobFailureMessage = (intl, failure) =>
  intl.formatMessage({id: REPORT_JOB_FAILURE_KEYS[failure] || 'reportJobFailureUnknown'});

/**
 * Mensagem mostrada assim que o pedido é aceito (`onEnqueued` de `exportReportViaQueue`).
 * `estimatedSeconds` de 0 significa "sem histórico ainda para estimar" (nunca um número
 * inventado, ver `ReportJobService.estimateSeconds` no servidor) -- mensagem genérica nesse caso.
 *
 * @param {object} intl instância do react-intl
 * @param {number} estimatedSeconds estimativa em segundos, 0 quando ainda não calculada
 * @return {string} texto traduzido
 */
export const reportJobQueuedMessage = (intl, estimatedSeconds) => {
  if (!estimatedSeconds) {
    return intl.formatMessage({id: 'reportJobQueued'});
  }
  return intl.formatMessage({id: 'reportJobQueuedEstimate'}, {estimate: formatDuration(intl, estimatedSeconds)});
};

/**
 * Mensagem mostrada assim que o pedido é aceito, cobrindo o caso do semáforo por hash
 * (ReportJobService.enqueue): duas pessoas pedindo o mesmo relatório (mesmo tipo, período e
 * dispositivos) recebem o job já pronto ou já em andamento de quem pediu primeiro, não um novo.
 * Se já veio concluído, o prazo estimado ("pronto em até ...") seria falso -- já está pronto agora.
 *
 * @param {object} intl instância do react-intl
 * @param {object} job pedido aceito pelo servidor
 * @return {string} texto traduzido
 */
export const reportJobEnqueuedMessage = (intl, job) => job.status === 'done' ?
  intl.formatMessage({id: 'reportJobAlreadyReady'}) :
  reportJobQueuedMessage(intl, job.estimatedSeconds);

/**
 * Mostra, no toast padrão do sistema, por `REPORT_ENQUEUED_TOAST_MS`, a mensagem de pedido aceito.
 *
 * @param {Function} addToast função `addToast` do react-toast-notifications
 * @param {object} intl instância do react-intl
 * @param {object} job pedido aceito pelo servidor
 */
export const notifyReportJobEnqueued = (addToast, intl, job) => {
  addToast(reportJobEnqueuedMessage(intl, job), {
    appearance: 'info',
    autoDismiss: true,
    autoDismissTimeout: REPORT_ENQUEUED_TOAST_MS,
  });
};

/**
 * Enfileira um relatório na fila assíncrona e devolve o pedido aceito. Cobre rota,
 * viagens, paradas, jornada, eventos e resumo.
 *
 * Não espera o relatório ficar pronto nem baixa o arquivo: quando termina, o servidor manda a
 * notificação, e o download é ação da pessoa, no botão da linha em "Relatórios Solicitados".
 *
 * `onEnqueued`, se passado, recebe o job assim que ele é aceito, com `estimatedSeconds` já
 * calculado, para quem chama mostrar o prazo estimado, já na unidade certa.
 *
 * `extra` carrega campo específico de um tipo de relatório -- `eventTypes` (Eventos), `daily`
 * (Resumo) -- que só esses tipos usam e os demais não precisam declarar.
 *
 * @param {string} reportType tipo do relatório
 * @param {string} format `xlsx` ou `pdf`
 * @param {Array<number>} deviceIds dispositivos pedidos
 * @param {Array<number>} groupIds grupos pedidos
 * @param {string|Date} from início do período
 * @param {string|Date} to fim do período
 * @param {function(object): void=} onEnqueued chamada com o pedido aceito
 * @param {boolean=} mail se o relatório também vai por e-mail
 * @param {object=} extra campos específicos do tipo de relatório
 * @return {Promise<object>} pedido aceito pelo servidor
 */
// Os botões de exportar não têm estado de carregamento: um clique duplo, ou um segundo clique enquanto o POST não
// voltou, enfileiraria o mesmo relatório duas vezes. Enquanto um pedido está em voo, um pedido igual recebe a mesma
// resposta; um pedido diferente (outro formato, período ou dispositivos) segue para o servidor.
let pendingExport = null;

export const exportReportViaQueue = (
    reportType, format, deviceIds, groupIds, from, to, onEnqueued, mail = false, extra = {}) => {
  const key = JSON.stringify([reportType, format, deviceIds, groupIds, from, to, mail, extra]);
  if (pendingExport && pendingExport.key === key) return pendingExport.promise;
  const promise = enqueueReportJob(reportType, format, deviceIds, groupIds, from, to, mail, extra)
      .then((job) => {
        if (onEnqueued) onEnqueued(job);
        return job;
      })
      .finally(() => {
        if (pendingExport && pendingExport.promise === promise) pendingExport = null;
      });
  pendingExport = {key, promise};
  return promise;
};

/**
 * Coordenada `[longitude, latitude]` da linha escolhida na lista do relatório de rota, para desenhar o pino no
 * mapa. `0` é uma coordenada válida; só a falta de um dos dois valores devolve `null`.
 *
 * @param {object} row linha do relatório
 * @return {Array<number>|null} par `[longitude, latitude]` ou `null`
 */
export const selectedRowCoordinate = (row) =>
  row && row.longitude != null && row.latitude != null ? [row.longitude, row.latitude] : null;

export const buildReportQuery = (params) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => query.append(key, item));
    } else if (value !== undefined && value !== null) {
      query.append(key, value);
    }
  });

  return query.toString();
};

/**
 * Largura de coluna proporcional ao maior texto entre o cabeçalho e as células.
 *
 * CONSTRAINT: percorre as linhas sem espalhar nada em `Math.max(...)`: o spread vira argumentos da chamada
 * e estoura a pilha com centenas de milhares de linhas.
 * PERFORMANCE: uma passada só sobre `data`, sem criar array intermediário.
 *
 * @param {Array<object>} data linhas do relatório
 * @param {string|function(object): *} accessor chave da coluna ou função que extrai o valor da linha
 * @param {string} headerText texto do cabeçalho
 * @return {number} largura em pixels
 */
export const getColumnWidth = (data, accessor, headerText) => {
  let cellLength = headerText.length;

  for (const row of data) {
    const value = typeof accessor === 'string' ? row[accessor] : accessor(row);
    const length = typeof value === 'number' ? value.toString().length : (value || '').length;
    if (length > cellLength) cellLength = length;
  }

  const magicSpacing = 10;
  return cellLength * magicSpacing;
};

export const memorizeReportForm = (args) => {
  const session = sessionStorage.getItem('reportForm');

  if (session) {
    sessionStorage.setItem(
        'reportForm',
        JSON.stringify({...JSON.parse(session), ...args}),
    );
  } else {
    sessionStorage.setItem('reportForm', JSON.stringify(args));
  }
};

export const getMemorizeReportForm = (...keys) => {
  let session = sessionStorage.getItem('reportForm');
  if (session) {
    session = JSON.parse(session);
    if (!keys.length) return session;
    return keys.reduce((acc, k) => ({...acc, [k]: session[k] || null}), {});
  }
  return null;
};

export const getDate = (date) => (date ? new Date(date) : null);

export const formatEventAttributes = (attributes) =>
  Object.entries(attributes || {})
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');

const COMMAND_SENT_TYPES = ['commandSent', 'queuedCommandSent'];

// Texto da coluna de observação do relatório de eventos. O comando enviado vem dos atributos que o servidor
// grava (comando completo, nome inteiro e e-mail já mascarado) e é montado aqui, no idioma da tela.
export const buildEventDetail = (event, {intl, alarmType, geofences, maintenances}) => {
  const detail = [];
  const attributes = event.attributes || {};

  event.type === 'alarm' &&
  detail.push(intl.formatMessage({id: 'reportAlarmType'}, {name: alarmType[attributes.alarm]}));

  event.geofenceId > 0 &&
  detail.push(intl.formatMessage({id: 'reportGeofenceEntry'}, {name: geofences[event.geofenceId] || event.geofenceId}));

  event.maintenanceId > 0 &&
  detail.push(
      intl.formatMessage({id: 'reportMaintenanceEntry'}, {name: maintenances[event.maintenanceId] || event.maintenanceId}),
  );

  if (!detail.length) {
    if (COMMAND_SENT_TYPES.includes(event.type) && attributes.command) {
      detail.push(
        attributes.userName ?
          intl.formatMessage(
              {id: 'reportCommandSentBy'},
              {command: attributes.command, name: attributes.userName, email: attributes.userEmail || ''},
          ) :
          intl.formatMessage({id: 'reportCommandSentNoUser'}, {command: attributes.command}),
      );
    } else {
      detail.push(attributes.observation || formatEventAttributes(attributes));
    }
  }

  return detail.join(', ');
};

// Texto da coluna de observação do relatório de rotas: o resultado de um comando, gravado pelo decodificador na
// posição que traz a resposta do rastreador. Quem enviou o comando fica só nos eventos.
export const buildRouteObservation = (position, intl) => {
  const attributes = (position && position.attributes) || {};
  const parts = [];

  attributes.result &&
  parts.push(intl.formatMessage({id: 'reportRouteResult'}, {result: attributes.result}));

  return parts.join('; ');
};

// Colunas de texto longo quebram linha em vez de alargar sem limite. As listas são virtualizadas (react-window)
// e precisam saber a altura de cada linha antes de desenhá-la, então a altura sai de uma estimativa do texto.
export const WRAP_COLUMN_MAX_WIDTH = 480;
const WRAP_CHAR_WIDTH = 8;
const WRAP_LINE_HEIGHT = 20;
const WRAP_HORIZONTAL_PADDING = 24;
const WRAP_VERTICAL_PADDING = 25;
const DEFAULT_ROW_HEIGHT = 45;

export const getWrappedRowHeight = (text, columnWidth, minHeight = DEFAULT_ROW_HEIGHT) => {
  const chars = String(text || '').length;
  const usable = Math.max(columnWidth - WRAP_HORIZONTAL_PADDING, WRAP_CHAR_WIDTH);
  const lines = Math.max(1, Math.ceil((chars * WRAP_CHAR_WIDTH) / usable));
  return Math.max(minHeight, lines * WRAP_LINE_HEIGHT + WRAP_VERTICAL_PADDING);
};

// Ao escolher uma linha do relatorio o mapa centraliza no ponto. A primeira linha leva o mapa ao zoom do relatorio;
// depois o zoom e o que a pessoa tem: se ela aproximar ou afastar, as proximas linhas ficam nesse zoom. Durante a
// animacao do proprio zoom vale o ultimo aplicado, nao o valor do meio do caminho.
export const REPORT_ROW_ZOOM = 15;

export const zoomForReportRow = (currentZoom, lastZoom, animating) => {
  if (lastZoom == null) return REPORT_ROW_ZOOM;
  if (animating) return lastZoom;
  return currentZoom || lastZoom;
};

export const goToReportRow = (instance, coordinate, lastZoomRef) => {
  const view = instance._instance.getView();
  const zoom = zoomForReportRow(view.getZoom(), lastZoomRef.current, view.getAnimating());
  lastZoomRef.current = zoom;
  instance.goTo(coordinate, zoom);
};

// Eventos e paradas mandam o proprio ponto ao mapa a cada linha escolhida; isso nao e um relatorio novo.
export const startsNewReport = (mapData) =>
  !mapData || !['event', 'stop'].includes(mapData.type) || mapData.data == null;
